/**
 * Shared identifier grammar for `predicateToFilterCondition.ts` and its JSON /
 * enum inverse-parse branches (`predicateJsonBranch.ts`, `predicateJsonBranchPg.ts`).
 *
 * Extracted to its own leaf module (rather than exported from
 * `predicateToFilterCondition.ts`) so the JSON-branch modules can import the
 * grammar WITHOUT creating a module cycle: `predicateToFilterCondition.ts`
 * imports `parseJsonPredicate` (a value) from the JSON-branch modules, so those
 * modules must not import a value back from `predicateToFilterCondition.ts` —
 * a cycle would leave `IDENT_PART` unresolved at the JSON-branch module's
 * top-level evaluation time.
 */

/**
 * Qualified identifier: optional bracket-quoted, double-quoted, backtick-
 * quoted, or bare schema/alias prefix, then a dot, then the column name in
 * the same forms.
 * Examples: `b.Status`, `Status`, `[My Table].[My Col]`, `"schema"."col"`,
 * `` `schema`.`col` ``.
 *
 * The backtick alternative is a deliberate, additive (accept-only-more,
 * never-reject-what-matched-before) extension made for this task: VQE's ADD
 * path always quotes the column via `quoteIdent`, which backtick-wraps for
 * MySQL (see `sqlIdentifierQuoting.ts`) — e.g. `` b.`meta` = 5 ``. Without
 * this alternative, EVERY MySQL predicate (not just JSON ones) would fail
 * `QUALIFIED_IDENT` and never round-trip through the edit chip; the JSON
 * branch added in this task needs backtick-quoted fields to work for MySQL
 * at all.
 */
export const IDENT_PART = /(?:\[(?:[^\]]+)\]|"(?:[^"]+)"|`(?:[^`]+)`|[A-Za-z_][A-Za-z0-9_$#@]*)/;

export const QUALIFIED_IDENT = new RegExp(
  `^((?:${IDENT_PART.source}\\.)?${IDENT_PART.source})$`,
);

/** Non-anchored source for embedding a qualified-identifier capture group
 *  inside a larger regex (JSON-shape matchers). */
export const QUALIFIED_IDENT_SRC = `(?:${IDENT_PART.source}\\.)?${IDENT_PART.source}`;
